import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { getSelectedMover, updateSelectedMover } from "@/store/mover/slice";
import { useUpdateMoverMutation } from "@/store/mover/apis";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";

interface OperatingPoliciesForm {
  services_offered: { name: string; enabled: boolean }[];
  signed_estimates: boolean;
  estimates_show_time_and_total: boolean;
  weekday_minimum_hours: number;
  weekend_minimum_hours: number;
  terms_and_conditions: string;
  payment_methods: string[];
  collect_deposit: boolean;
  deposit_amount: number;
  deposit_percentage: number;
  cancelation_notice_days: number;
  charge_cc_fee: boolean;
  cc_fee_percentage: number;
  interested_in_payments: boolean;
  automated_review_texts: boolean;
  deposit_link: string;
}

const SERVICE_OPTIONS = [
  {
    id: "residential",
    label: "Residential Moves",
    description: "May include packing",
  },
  {
    id: "commercial",
    label: "Commercial Moves",
    description: "Office and other commercial moves",
  },
  {
    id: "storage",
    label: "Storage Moves",
    description: "Moves out of storage units",
  },
  { id: "labor", label: "Labor Only (no trucks)" },
  {
    id: "items",
    label: "One (or a few) Item Moves",
    description: "Moves that include a single or few item",
  },
  { id: "piano", label: "Piano Moves", description: "Specialized piano moves" },
  { id: "delivery", label: "Delivery", description: "Deliveries" },
  {
    id: "junk",
    label: "Junk Removal",
    description: "Removing junk from a property",
  },
  // {
  //   id: "trash",
  //   label: "Trash Removal",
  //   description: "Removing trash from a site",
  // },
  {
    id: "custom",
    label: "Custom Service",
    description: "Something that doesn't fit into other categories",
  },
];

const PAYMENT_METHOD_OPTIONS = [
  { id: "card", label: "Credit/Debit Card" },
  { id: "cash", label: "Cash" },
  { id: "check", label: "Check" },
  { id: "zelle", label: "Zelle" },
  { id: "venmo", label: "Venmo" },
  { id: "paypal", label: "Paypal" },
];

const OperatingPolicies: React.FC = () => {
  const dispatch = useDispatch();
  const selectedMover = useSelector(getSelectedMover);
  const { toast } = useToast();
  const [updateMoverApi, { isLoading }] = useUpdateMoverMutation();
  const [collectDeposit, setCollectDeposit] = useState(
    selectedMover?.mover_configuration?.collect_deposit || false
  );
  const [chargeCcFee, setChargeCcFee] = useState(
    (selectedMover?.mover_configuration?.cc_fee_percentage || 0) > 0
  );

  const form = useForm<OperatingPoliciesForm>({
    defaultValues: {
      services_offered:
        selectedMover?.mover_configuration?.services_offered || [],
      signed_estimates:
        selectedMover?.mover_configuration?.signed_estimates || false,
      estimates_show_time_and_total:
        selectedMover?.mover_configuration?.estimates_show_time_and_total ||
        false,
      weekday_minimum_hours:
        selectedMover?.mover_configuration?.weekday_minimum_hours || 2,
      weekend_minimum_hours:
        selectedMover?.mover_configuration?.weekend_minimum_hours || 3,
      terms_and_conditions:
        selectedMover?.mover_configuration?.terms_and_conditions || "",
      payment_methods:
        selectedMover?.mover_configuration?.payment_methods || [],
      collect_deposit: collectDeposit,
      deposit_amount: selectedMover?.mover_configuration?.deposit_amount || 100,
      deposit_percentage:
        selectedMover?.mover_configuration?.deposit_percentage || 10,
      cancelation_notice_days:
        selectedMover?.mover_configuration?.cancelation_notice_days || 3,
      deposit_link: selectedMover?.mover_configuration?.deposit_link || "",
      charge_cc_fee: chargeCcFee,
      cc_fee_percentage:
        selectedMover?.mover_configuration?.cc_fee_percentage || 3,
      interested_in_payments:
        selectedMover?.mover_configuration?.interested_in_payments || false,
      automated_review_texts:
        selectedMover?.mover_configuration?.automated_review_texts || false,
    },
  });

  // Watch for changes to collect_deposit and charge_cc_fee
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "collect_deposit") {
        setCollectDeposit(value.collect_deposit || false);
      }
      if (name === "charge_cc_fee") {
        setChargeCcFee(value.charge_cc_fee || false);
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch]);

  const toggleService = (id: string) => {
    const currentServices = form.getValues("services_offered") || [];

    // Check if the service already exists in the array
    const existingIndex = currentServices.findIndex(
      (service) => service.name === id
    );

    if (existingIndex === -1) {
      // Service doesn't exist, add it as an object
      form.setValue("services_offered", [
        ...currentServices,
        { name: id, enabled: true },
      ]);
    } else {
      // Service exists, toggle its enabled status if it's an object
      // or replace the string with an object if it's a string
      const updatedServices = [...currentServices];

      if (typeof updatedServices[existingIndex] === "string") {
        // Convert string to object with enabled=false (toggling from true to false)
        updatedServices[existingIndex] = { name: id, enabled: false };
      } else {
        // Toggle the enabled property
        updatedServices[existingIndex] = {
          ...updatedServices[existingIndex],
          enabled: !updatedServices[existingIndex].enabled,
        };
      }

      form.setValue("services_offered", updatedServices);
    }
  };

  const onSubmit = async (data: OperatingPoliciesForm) => {
    try {
      const updatedMover = await updateMoverApi({
        data: {
          mover_configuration_attributes: {
            ...selectedMover?.mover_configuration,
            services_offered: data.services_offered,
            signed_estimates: data.signed_estimates,
            estimates_show_time_and_total: data.estimates_show_time_and_total,
            weekday_minimum_hours: data.weekday_minimum_hours,
            weekend_minimum_hours: data.weekend_minimum_hours,
            terms_and_conditions: data.terms_and_conditions,
            payment_methods: data.payment_methods,
            collect_deposit: data.collect_deposit,
            deposit_amount: data.collect_deposit ? data.deposit_amount : 0,
            deposit_percentage: data.collect_deposit
              ? data.deposit_percentage
              : 0,
            cancelation_notice_days: data.collect_deposit
              ? data.cancelation_notice_days
              : 0,
            cc_fee_percentage: data.charge_cc_fee ? data.cc_fee_percentage : 0,
            interested_in_payments: data.interested_in_payments,
            automated_review_texts: data.automated_review_texts,
            deposit_link: data.deposit_link,
          },
        },
      }).unwrap();

      dispatch(updateSelectedMover(updatedMover));

      toast({
        title: "Operating policies updated",
        description: "Your operating policies have been successfully updated.",
      });
    } catch (error) {
      console.error("Error updating operating policies:", error);
      toast({
        title: "Error",
        description:
          "There was an error updating your operating policies. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="w-full max-w-2xl px-8 py-4 flex flex-col justify-start items-start gap-6">
          <h2 className="text-lg font-bold">Operating Policies</h2>

          {/* Services Section */}
          <Card className="w-full shadow-none border-none p-0">
            <CardHeader>
              <CardTitle>What services do you offer?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="services_offered"
                render={() => (
                  <FormItem className="space-y-4">
                    {/* <FormLabel>What services do you offer?</FormLabel> */}
                    <div className="space-y-3">
                      {SERVICE_OPTIONS.map((option) => (
                        <FormField
                          key={option.id}
                          control={form.control}
                          name="services_offered"
                          render={({ field }) => {
                            return (
                              <FormItem
                                key={option.id}
                                className="flex flex-row items-center gap-4"
                              >
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.some(
                                      (service) =>
                                        service.name === option.id &&
                                        service.enabled
                                    )}
                                    onCheckedChange={() => {
                                      toggleService(option.id);
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  <span className="font-semibold">
                                    {option.label}
                                  </span>
                                  {option.description && (
                                    <FormDescription className="text-sm text-stone-600">
                                      {option.description}
                                    </FormDescription>
                                  )}
                                </FormLabel>
                              </FormItem>
                            );
                          }}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Policies Section */}
          <Card className="w-full">
            <CardHeader>
              <CardTitle>Policies</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="signed_estimates"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Do you want customers to sign estimates before you book
                        a job?
                      </FormLabel>
                      <FormDescription>
                        Customers will be able to sign estimates via their
                        customer portal link sent to them by text and email
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="estimates_show_time_and_total"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        On hourly moves, do you want customers to see estimated
                        hours and total in the estimate?
                      </FormLabel>
                      <FormDescription>
                        Most movers answer no but your preference may be
                        different
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="weekday_minimum_hours"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Weekday Minimum Hours</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseInt(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        Minimum hours for weekday moves
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="weekend_minimum_hours"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Weekend Minimum Hours</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseInt(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        Minimum hours for weekend moves
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="terms_and_conditions"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Terms and Conditions</FormLabel>
                    <FormDescription>
                      These will be displayed on the customer portal and emailed
                      to customers when they book a job. If this is longer than
                      a few lines, please consider uploading this as a document
                      that is included in estimates and confirmations. You can
                      upload documents here:{" "}
                      <Link
                        to="/mover-settings/documents"
                        className="text-blue-500 hover:underline"
                        target="_blank"
                      >
                        link to documents
                      </Link>
                    </FormDescription>
                    <FormControl>
                      <Textarea
                        placeholder="Enter your terms and conditions here..."
                        className="min-h-[200px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Payment Section */}
          <Card className="w-full">
            <CardHeader>
              <CardTitle>Payment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="payment_methods"
                render={() => (
                  <FormItem>
                    <FormLabel>What forms of payment do you accept?</FormLabel>
                    <div className="space-y-2">
                      {PAYMENT_METHOD_OPTIONS.map((option) => (
                        <FormField
                          key={option.id}
                          control={form.control}
                          name="payment_methods"
                          render={({ field }) => {
                            return (
                              <FormItem
                                key={option.id}
                                className="flex flex-row items-start space-x-3 space-y-0"
                              >
                                <FormControl>
                                  <Checkbox
                                    checked={field.value?.includes(option.id)}
                                    onCheckedChange={(checked) => {
                                      return checked
                                        ? field.onChange([
                                            ...field.value,
                                            option.id,
                                          ])
                                        : field.onChange(
                                            field.value?.filter(
                                              (value) => value !== option.id
                                            )
                                          );
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  {option.label}
                                </FormLabel>
                              </FormItem>
                            );
                          }}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="collect_deposit"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Do you collect deposits?
                      </FormLabel>
                      <FormDescription>
                        To process deposits, we will need you to get setup with
                        our payment processor, Fiserv (please see below).
                        Approval typically takes 48 to 72 hours.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {collectDeposit && (
                <div className="space-y-4 pl-4 border-l-2 border-gray-200">
                  <FormField
                    control={form.control}
                    name="deposit_amount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Typical Deposit Amount ($)</FormLabel>
                        <FormDescription>
                          This can be changed per job
                        </FormDescription>
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseInt(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="deposit_percentage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Typical Deposit Percentage (%)</FormLabel>
                        <FormDescription>
                          For large moves (can be changed per job)
                        </FormDescription>
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseInt(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="cancelation_notice_days"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cancellation Notice (Days)</FormLabel>
                        <FormDescription>
                          How many days in advance can customers cancel to get a
                          refund of the deposit?
                        </FormDescription>
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseInt(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="deposit_link"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Deposit Link</FormLabel>
                        <FormDescription>
                          A link where customers can pay their deposit directly
                        </FormDescription>
                        <FormControl>
                          <Input
                            type="url"
                            {...field}
                            onChange={(e) => field.onChange(e.target.value)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <FormField
                control={form.control}
                name="charge_cc_fee"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Do you charge a credit card processing fee (surcharge)?
                      </FormLabel>
                      <FormDescription>
                        Please note that if you are in CT, ME, MA, or PR,
                        surcharges are prohibited by law. If your state allows
                        surcharges, you are required to disclose them to the
                        customer upfront.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {chargeCcFee && (
                <div className="pl-4 border-l-2 border-gray-200">
                  <FormField
                    control={form.control}
                    name="cc_fee_percentage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Credit Card Processing Fee (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={0}
                            max={100}
                            step={0.01}
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseFloat(e.target.value))
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}

              <FormField
                control={form.control}
                name="interested_in_payments"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Are you interested in processing payments through the
                        software?
                      </FormLabel>
                      <FormDescription>
                        We offer payments through Fiserv/First Data (NYSE: FI),
                        the largest payments processor in the US. They offer
                        competitive rates starting at 2.95% + 5c per transaction
                        and can generally meet or beat your rate.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Reviews Section */}
          <Card className="w-full">
            <CardHeader>
              <CardTitle>Reviews</CardTitle>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="automated_review_texts"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Do you want the software to send a text requesting a
                        review after a job is completed?
                      </FormLabel>
                      <FormDescription>
                        We recommend this so you are collecting reviews on every
                        job
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Button type="submit" disabled={isLoading}>
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default OperatingPolicies;
